// The calendar's enrichment language: which callouts a meeting shows, in what order, and with which tone.
// Every fact comes from the API's MeetingFacts; this only chooses the words and the priority (display).
import type { MeetingView } from '../types/calendar'
import type { MeetingThreat } from '../types/threats'
import { formatCompact } from './format'

export type MeetingKind = 'client' | 'external' | 'internal' | 'solo'
/** `threat` is the only tone that outranks every other callout: a rival is in the room. */
export type CalloutTone = 'threat' | 'action' | 'signal' | 'watch' | 'stage' | 'quiet'
export interface Callout { key: string; label: string; tone: CalloutTone; short: string }

const STAGE_WORDS: Record<string, string> = {
  lead: 'Lead', opportunity: 'Opportunity', proposal: 'Proposal out', negotiation: 'Negotiating', won: 'Won', lost: 'Lost', delivery: 'In delivery', dormant: 'Gone quiet', none: 'No stage',
}
export const stageWords = (stage: string) => STAGE_WORDS[stage] ?? stage

export const PERSONA_WORDS: Record<string, string> = {
  champion: 'Champion', influencer: 'Influencer', challenger: 'Challenger', 'decision-maker': 'Decision maker', gatekeeper: 'Gatekeeper', user: 'User', other: 'Other',
}
export const ROLE_WORDS: Record<string, string> = {
  customer: 'Customer', prospect: 'Prospect', vendor: 'Vendor', partner: 'Partner', colleague: 'Colleague', competitor: 'Competitor', other: 'Other',
}

/** Who is in the room that is not a colleague. */
export const externalContacts = (m: MeetingView) => m.contacts.filter((c) => c.role !== 'colleague')

/** Is a rival in this room? The one question the Meetings page answers before any other. */
export const isThreat = (m: MeetingView) => !!m.facts?.threat

/** The API says what kind of room it is; without facts (an older payload) the room itself does. */
export function kindOf(m: MeetingView): MeetingKind {
  if (m.facts) return m.facts.kind as MeetingKind
  if (m.meeting.companyId || m.companyName || externalContacts(m).length > 0) return 'client'
  return m.meeting.attendeeEmails.length === 0 ? 'solo' : 'internal'
}

/** The words for a rival in the room. Named when we know who; hedged only when we do not. */
export function threatLabel(t: MeetingThreat): string {
  const who = t.competitor
  if (t.severity === 'watch' || t.needsJudgement) return `Possible competitor in the room: ${who}`
  return `Competitor in the room: ${who}`
}

export function calloutsFor(m: MeetingView, now: Date): Callout[] {
  const out: Callout[] = []
  const upcoming = new Date(m.meeting.start).getTime() >= now.getTime() && !m.meeting.isCancelled
  const f = m.facts
  const accountKnown = !!(m.meeting.companyId && m.companyName)
  const transcript = f?.transcript ?? (m.transcriptId ? { transcriptId: m.transcriptId } : null)
  // a rival in the room is read before anything else: this meeting is a threat, not an opportunity
  if (f?.threat) out.push({ key: 'threat', label: threatLabel(f.threat), tone: 'threat', short: '!' })
  if (transcript) out.push({ key: 'transcript', label: 'Transcript linked', tone: 'signal', short: 'T' })
  if (upcoming && accountKnown) out.push({ key: 'prep', label: 'Prep this', tone: 'action', short: 'P' })
  if (f?.firstWithAccount) out.push({ key: 'first', label: `First meeting with ${m.companyName ?? 'this account'}`, tone: 'signal', short: '1' })
  const decision = f ? f.decisionMakerInRoom : m.contacts.some((c) => c.persona === 'decision-maker')
  if (decision) out.push({ key: 'decision', label: 'Decision maker in the room', tone: 'signal', short: 'D' })
  if (f && f.resurfacingContactIds.length > 0) {
    const names = f.resurfacingContactIds.map((id) => m.contacts.find((c) => c.personId === id)?.name).filter(Boolean)
    out.push({ key: 'resurfacing', label: names.length === 1 ? `${names[0]} resurfacing` : `${f.resurfacingContactIds.length} quiet contacts resurfacing`, tone: 'watch', short: 'R' })
  }
  if (f && f.movedContactIds.length > 0) {
    const name = m.contacts.find((c) => c.personId === f.movedContactIds[0])?.name
    out.push({ key: 'moved', label: name ? `${name} may have moved` : 'A contact may have moved', tone: 'watch', short: 'M' })
  }
  if (f && f.openPursuits.length > 0) {
    const p = f.openPursuits[0]
    const amount = p.amount != null ? ` · $${formatCompact(p.amount)}` : ''
    out.push({ key: 'stage', label: `${stageWords(p.stage)}${amount}`, tone: 'stage', short: stageWords(p.stage)[0] })
  }
  if (f && f.tacticsDue.length > 0) out.push({ key: 'tactic', label: `Tactic due: ${f.tacticsDue[0].tacticName}`, tone: 'action', short: 'X' })
  return out
}

/** A stable 1..8 swatch for an account so the same client is the same colour on every view; 0 = no account. */
export function accountSwatch(companyId: string | null | undefined): number {
  if (!companyId) return 0
  let h = 0
  for (let i = 0; i < companyId.length; i++) h = (h * 31 + companyId.charCodeAt(i)) >>> 0
  return (h % 8) + 1
}

/** Relationship strength, in words a rep uses. */
export function strengthWords(s: number | null | undefined): 'strong' | 'warm' | 'faint' | 'unknown' {
  if (s == null) return 'unknown'
  return s >= 0.5 ? 'strong' : s >= 0.15 ? 'warm' : 'faint'
}
